import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';

/**
 * Functional Interceptor to handle global HTTP errors.
 * Standardizes error messaging and logging across the application.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            let errorMessage = 'An unknown error occurred!';

            if (error.error instanceof ErrorEvent) {
                // Client-side or network error
                errorMessage = `Client Error: ${error.error.message}`;
            } else {
                // Backend / Server error
                switch (error.status) {
                    case 401:
                        // Let the auth interceptor handle 401s (refresh + delayed logout)
                        return throwError(() => error);
                    case 403:
                        // Let the auth interceptor try refresh for token-expired 403 responses.
                        return throwError(() => error);
                    case 404:
                        errorMessage = 'Resource not found.';
                        break;
                    case 400:
                        if (error.error?.errors) {
                            let fieldMessages: string[] = [];
                            if (Array.isArray(error.error.errors)) {
                                // DRF many=True lists of dicts
                                fieldMessages = error.error.errors.map((e: any) => {
                                    if (typeof e === 'object' && e !== null) {
                                        return Object.entries(e).filter(([k]) => k !== 'index').map(([k, v]) => String(v)).join(', ');
                                    }
                                    return String(e);
                                }).filter((msg: string) => msg.trim().length > 0);
                            } else {
                                fieldMessages = Object.values(error.error.errors)
                                    .flatMap((value: any) => Array.isArray(value) ? value : [value])
                                    .map((item: any) => String(item));
                            }
                            if (fieldMessages.length) {
                                errorMessage = fieldMessages.join(' | ');
                                break;
                            }
                        }

                        if (error.error?.message) {
                            errorMessage = error.error.message;
                        } else {
                            errorMessage = 'Validation or client error occurred.';
                        }
                        break;
                    case 500:
                        errorMessage = 'Internal Server Error: Please try again later.';
                        break;
                    default:
                        errorMessage = error.error?.message || `Error Code: ${error.status}\nMessage: ${error.message}`;
                }
            }

            console.error('Global Error Interceptor:', {
                status: error.status,
                url: error.url,
                message: errorMessage,
                originalError: error
            });

            // Preserve original HTTP error details alongside a friendly message.
            const wrappedError = new Error(errorMessage) as Error & { httpError?: HttpErrorResponse };
            wrappedError.httpError = error;
            return throwError(() => wrappedError);
        })
    );
};
